import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({ error, errorInfo });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleGoHome = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-[#08090c] flex items-center justify-center p-6">
                    {/* Background grid */}
                    <div
                        className="absolute inset-0 opacity-20 pointer-events-none"
                        style={{
                            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)`,
                            backgroundSize: '40px 40px'
                        }}
                    />

                    <div className="relative z-10 max-w-lg w-full text-center">
                        {/* Glowing error orb */}
                        <div className="relative w-24 h-24 mx-auto mb-8">
                            <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl animate-pulse" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-20 h-20 bg-[#0F131F] border border-red-500/30 rounded-full flex items-center justify-center">
                                    <AlertTriangle className="w-10 h-10 text-red-400" />
                                </div>
                            </div>
                        </div>

                        <h1 className="text-3xl font-bold text-white font-mono mb-3 tracking-tight">
                            SYSTEM_ERROR
                        </h1>
                        <p className="text-slate-400 mb-2 text-sm font-mono">
                            Something unexpected went wrong. Don't worry — your data is safe.
                        </p>
                        <p className="text-slate-600 text-xs font-mono mb-8">
                            Error: {this.state.error?.message || 'Unknown error'}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={this.handleReset}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-xl transition-all font-mono text-sm uppercase tracking-wider"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Try Again
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-xl transition-all font-mono text-sm uppercase tracking-wider"
                            >
                                <Home className="w-4 h-4" />
                                Go Home
                            </button>
                        </div>

                        {/* Technical details (collapsed) */}
                        {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                            <details className="mt-8 text-left">
                                <summary className="text-xs text-slate-500 font-mono cursor-pointer hover:text-slate-300 transition-colors">
                                    STACK_TRACE
                                </summary>
                                <pre className="mt-2 p-4 bg-white/5 border border-white/10 rounded-lg text-xs text-red-400 font-mono overflow-auto max-h-48">
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
